/**
 * Cart — full cart page (route /cart and /:lang/cart).
 *
 * Sections (top → bottom):
 *   • Header with item count
 *   • Line items table: thumbnail, name+variation, qty stepper, unit
 *     price, line total, remove
 *   • Totals card: subtotal (in display currency), shipping note, payment
 *     buttons (Stripe, Bank, QRIS for IDR, PayPal if enabled)
 *   • Empty state with CTA back to the Collection
 *
 * Pricing math: each item carries its OWN base currency, so subtotals are
 * computed by bridging every line through USD using the live FX context,
 * then rendering in the user's chosen display currency. This means a cart
 * mixing IDR-base + USD-base products totals correctly.
 */
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Trash2, Plus, Minus, ShoppingBag, CreditCard, Building2, QrCode, Loader2 } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useCurrency } from "../context/CurrencyContext";
import { useLang } from "../context/LanguageContext";
import { prefixLocale } from "../lib/i18nRouter";
import api from "../lib/api";
import { getMetaAttribution } from "../lib/metaAttribution";
import TX from "../components/TX";
import Seo from "../components/Seo";
import QrisPaymentModal from "../components/QrisPaymentModal";

const Cart = () => {
  const { items, updateQty, removeItem, clearCart, count } = useCart();
  const { format, currency, rates } = useCurrency();
  const { lang } = useLang();
  const lp = (p) => prefixLocale(p, lang);
  const navigate = useNavigate();
  const [busyStripe, setBusyStripe] = useState(false);
  const [qrisOpen, setQrisOpen] = useState(false);

  // Bridge every item to USD so we have a single number we can render in
  // any currency. The math: usd = unit_price / rates[base_currency].
  const toUsd = (amount, fromCurrency) => {
    const fromRate = rates?.[fromCurrency || "USD"] || 1;
    return Number(amount || 0) / fromRate;
  };
  const subtotalUsd = items.reduce(
    (acc, i) => acc + toUsd(i.unit_price, i.base_currency) * i.quantity,
    0,
  );

  const startStripeCart = async () => {
    if (items.length === 0) return;
    setBusyStripe(true);
    try {
      const { data } = await api.post("/checkout/cart-session", {
        items: items.map((i) => ({
          gemstone_id: i.gemstone_id,
          variation_id: i.variation_id,
          quantity: i.quantity,
        })),
        currency,
        origin_url: window.location.origin,
        ...getMetaAttribution(),
      });
      if (data?.url) {
        window.location.href = data.url;
      } else {
        toast.error("Stripe session unavailable. Please try Bank Transfer.");
        setBusyStripe(false);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Checkout failed");
      setBusyStripe(false);
    }
  };

  if (items.length === 0) {
    return (
      <div data-testid="cart-empty" className="min-h-[60vh] flex items-center justify-center px-6 py-20">
        <Seo title="Cart" description="Your OPIKJEWELS shopping cart." path="/cart" noIndex />
        <div className="text-center max-w-md">
          <ShoppingBag size={42} className="text-gold mx-auto mb-6 opacity-60" />
          <div className="font-accent text-[10px] tracking-[0.28em] uppercase text-gold mb-2"><TX>Your Cart</TX></div>
          <h1 className="font-heading text-3xl text-ivory mb-3"><TX>Awaiting your selection.</TX></h1>
          <p className="text-white/55 text-sm mb-8 leading-relaxed">
            <TX>Browse the Collection and add gemstones — variations and quantities are remembered until you complete checkout.</TX>
          </p>
          <Link
            to={lp("/collection")}
            data-testid="cart-browse-link"
            className="inline-block bg-gold text-black font-accent uppercase tracking-[0.28em] text-xs px-7 py-3 hover:bg-ivory transition-colors"
          >
            <TX>Browse Collection</TX>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="cart-page" className="max-w-5xl mx-auto px-6 py-16">
      <Seo title="Cart" description="Your OPIKJEWELS shopping cart." path="/cart" noIndex />
      <div className="flex items-end justify-between border-b border-white/10 pb-6 mb-8">
        <div>
          <div className="font-accent text-[10px] tracking-[0.28em] uppercase text-gold mb-2"><TX>Your Cart</TX></div>
          <h1 className="font-heading text-4xl text-ivory">{count} <TX>{count === 1 ? "item" : "items"}</TX></h1>
        </div>
        <button
          data-testid="cart-clear"
          onClick={() => { if (window.confirm("Empty cart?")) clearCart(); }}
          className="text-white/45 hover:text-red-400 transition-colors font-accent text-[10px] tracking-[0.28em] uppercase"
        >
          <TX>Clear</TX>
        </button>
      </div>

      {/* Line items */}
      <div className="space-y-3 mb-10">
        {items.map((it) => {
          const lineTotalBase = it.unit_price * it.quantity;
          return (
            <div
              key={it.key}
              data-testid={`cart-row-${it.gemstone_id}-${it.variation_id || "base"}`}
              className="grid grid-cols-[80px_1fr_auto] gap-4 items-center bg-onyx border border-white/10 p-4"
            >
              <div className="w-20 h-20 bg-noir border border-white/10 overflow-hidden flex-shrink-0">
                {it.image_url ? (
                  <img src={it.image_url} alt={it.name} className="w-full h-full object-cover" />
                ) : null}
              </div>
              <div className="min-w-0">
                <Link
                  to={lp(`/gemstone/${it.gemstone_id}`)}
                  className="text-ivory font-heading text-lg truncate hover:text-gold transition-colors"
                >
                  {it.name}
                </Link>
                {it.variation_label && (
                  <div className="text-white/55 text-xs mt-1">{it.variation_label}</div>
                )}
                <div className="text-white/40 text-[10px] mt-1 font-mono">
                  {format(it.unit_price, it.base_currency)} <TX>each</TX>
                </div>
                <button
                  data-testid={`cart-remove-${it.key}`}
                  onClick={() => removeItem(it.key)}
                  className="text-white/40 hover:text-red-400 transition-colors text-xs mt-2 inline-flex items-center gap-1"
                >
                  <Trash2 size={11} /> <TX>Remove</TX>
                </button>
              </div>
              <div className="flex flex-col items-end gap-2">
                <div className="flex items-center gap-2 border border-white/15">
                  <button
                    data-testid={`cart-dec-${it.key}`}
                    onClick={() => updateQty(it.key, it.quantity - 1)}
                    className="px-2 py-1.5 text-white/65 hover:text-gold transition-colors"
                  >
                    <Minus size={12} />
                  </button>
                  <input
                    data-testid={`cart-qty-${it.key}`}
                    type="number"
                    min="0"
                    value={it.quantity}
                    onChange={(e) => updateQty(it.key, Number(e.target.value))}
                    className="bg-transparent w-10 text-center text-ivory text-sm focus:outline-none"
                  />
                  <button
                    data-testid={`cart-inc-${it.key}`}
                    onClick={() => updateQty(it.key, it.quantity + 1)}
                    disabled={it.stock != null && it.quantity >= it.stock}
                    className="px-2 py-1.5 text-white/65 hover:text-gold transition-colors disabled:opacity-30"
                  >
                    <Plus size={12} />
                  </button>
                </div>
                <div className="font-heading text-base text-ivory whitespace-nowrap">
                  {format(lineTotalBase, it.base_currency)}
                </div>
                {it.stock != null && (
                  <div className="text-white/35 text-[10px]">
                    {it.stock} <TX>in stock</TX>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Totals + CTA */}
      <div className="bg-onyx border border-gold/30 p-7 space-y-5">
        <div className="flex items-baseline justify-between">
          <span className="font-accent text-[10px] tracking-[0.28em] uppercase text-white/45"><TX>Subtotal</TX></span>
          <span data-testid="cart-subtotal" className="font-heading text-3xl text-gold">
            {format(subtotalUsd, "USD")}
          </span>
        </div>
        <p className="text-white/45 text-xs leading-relaxed">
          <TX>Shipping is calculated on the checkout page based on the destination country. Taxes, if any, are itemized at payment time.</TX>
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <button
            data-testid="cart-stripe-btn"
            onClick={startStripeCart}
            disabled={busyStripe}
            className="flex items-center justify-center gap-2 bg-gold text-black font-accent uppercase tracking-[0.28em] text-xs px-5 py-4 hover:bg-ivory transition-colors disabled:opacity-50"
          >
            {busyStripe ? <Loader2 className="animate-spin" size={14} /> : <CreditCard size={14} />}
            {busyStripe ? <TX>Redirecting…</TX> : <TX>Checkout · Card</TX>}
          </button>
          {currency === "IDR" && (
            <button
              data-testid="cart-qris-btn"
              onClick={() => setQrisOpen(true)}
              className="flex items-center justify-center gap-2 border border-gold text-gold font-accent uppercase tracking-[0.28em] text-xs px-5 py-4 hover:bg-gold hover:text-black transition-colors"
            >
              <QrCode size={14} /> <TX>Pay with QRIS</TX>
            </button>
          )}
          <button
            data-testid="cart-bank-btn"
            onClick={() => { toast.info("For bank-wire on a multi-line order, our concierge will email full instructions."); navigate(lp("/concierge")); }}
            className="flex items-center justify-center gap-2 border border-white/25 text-white/75 font-accent uppercase tracking-[0.28em] text-xs px-5 py-4 hover:border-ivory hover:text-ivory transition-colors sm:col-span-2"
          >
            <Building2 size={14} /> <TX>Bank Wire — Request Instructions</TX>
          </button>
        </div>
      </div>

      <QrisPaymentModal
        open={qrisOpen}
        onClose={() => setQrisOpen(false)}
        amountIdr={subtotalUsd * (rates?.IDR || 0)}
        gemstoneName={`OPIKJEWELS Order · ${count} item${count === 1 ? "" : "s"}`}
        reference={`CART-${Date.now().toString().slice(-6)}`}
        onConfirmPaid={() => {
          setQrisOpen(false);
          clearCart();
          navigate(lp("/"));
        }}
      />
    </div>
  );
};

export default Cart;
